import { useState, useEffect, useContext } from "react";
import Title from "../component/Title";
import GlobalContext from "../GlobalContext";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";

function AccountsAdmin() {
  const { globalURL } = useContext(GlobalContext);
  const url = globalURL;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [players, setPlayers] = useState([]);

  Axios.defaults.withCredentials = true;
  const authHeader = {
    headers: { "auth-token": localStorage.getItem("token") },
  };

  const fetchAccounts = () => {
    Axios.get(url + "/users/players", authHeader)
      .then((res) => setAccounts(res.data))
      .catch(() => navigate("/login"));
  };

  useEffect(() => {
    if (!localStorage.token) {
      navigate("/login");
      return;
    }
    Promise.all([
      Axios.get(url + "/users/players", authHeader),
      Axios.get(url + "/players"),
    ])
      .then(([accRes, playersRes]) => {
        setAccounts(accRes.data);
        setPlayers(
          playersRes.data.map((p) => ({
            label: `${p.firstname || ""} ${p.lastname || ""}`.trim() || p._id,
            value: p._id,
          }))
        );
      })
      .catch(() => navigate("/login"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const link = (accountId, playerID) => {
    Axios.patch(
      url + "/users/" + accountId + "/link-player",
      { playerID },
      authHeader
    )
      .then(() => {
        enqueueSnackbar(playerID ? "Account linked." : "Account unlinked.", {
          variant: "success",
        });
        fetchAccounts();
      })
      .catch(() =>
        enqueueSnackbar("Could not update link.", { variant: "error" })
      );
  };

  return (
    <div className="min-h-screen">
      <div className="container">
        <div className="bgRed sm:w-[85%] lg:w-[60%] relative flex justify-center flex-col items-center p-4 border-black border-8 rounded-3xl my-8 sm-shadow">
          <Title word="Accounts" />
        </div>
        <div className="content my-8 justify-start items-center sm:w-[95%] lg:w-[80%] sm:pl-4 sm:pr-4 lg:pl-10 lg:pr-10 pt-10 pb-10 black-bg border-black border-8">
          <div className="relative text-center w-fit sm:mb-4 lg:mb-8 text-4xl font-extrabold stats-font p-2">
            Player Accounts
            <span className="absolute w-[100%] h-1 bottom-0 left-[-50%] translate-x-[50%] rounded-full bg-white"></span>
          </div>

          {loading ? (
            <CircularProgress />
          ) : accounts.length === 0 ? (
            <p className="text-white/70">No player accounts yet.</p>
          ) : (
            <div className="flex flex-col gap-4 w-full">
              {accounts.map((acc) => (
                <AccountRow
                  key={acc._id}
                  account={acc}
                  players={players}
                  onLink={link}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountRow({ account, players, onLink }) {
  const linkedPlayer = account.playerID;
  const linkedLabel = linkedPlayer
    ? `${linkedPlayer.firstname || ""} ${linkedPlayer.lastname || ""}`.trim()
    : null;

  const [selected, setSelected] = useState(
    linkedPlayer
      ? { label: linkedLabel || linkedPlayer._id, value: linkedPlayer._id }
      : null
  );

  return (
    <div className="flex sm:flex-col lg:flex-row lg:items-center gap-3 border-b border-white/10 py-3">
      <div className="lg:w-1/3 text-white">
        <div className="font-bold">
          {account.firstname} {account.lastname}
        </div>
        <div className="text-white/60 text-sm">
          {account.phone}
          {account.email ? ` · ${account.email}` : ""}
        </div>
      </div>
      <div className="lg:w-1/3">
        {linkedLabel ? (
          <span className="text-green-400 text-sm">Linked: {linkedLabel}</span>
        ) : (
          <span className="text-yellow-400 text-sm">Not linked</span>
        )}
      </div>
      <div className="lg:w-1/3 flex gap-2 items-center">
        <Autocomplete
          className="flex-1"
          options={players}
          value={selected}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          onChange={(e, v) => setSelected(v)}
          renderInput={(params) => (
            <TextField {...params} variant="standard" label="Player" />
          )}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => onLink(account._id, selected ? selected.value : null)}
        >
          {selected ? "Link" : "Unlink"}
        </Button>
      </div>
    </div>
  );
}

export default AccountsAdmin;
